import { FC, memo, ReactNode } from 'react'
import { useTranslate } from 'react-polyglot'
import { tokens } from '@getgo/chameleon-core/next/goto-admin'
import { CloseOutlinedIcon } from '@getgo/chameleon-icons/react'
import { IconButton, Tooltip, Typography } from '@getgo/chameleon-web-react-wrapper'
import { Box, Drawer as MuiDrawer, DrawerProps as MuiDrawerProps, Grid, makeStyles, SvgIcon } from '@material-ui/core'

import { BANNER_HEIGHT, DRAWER_WIDTH } from 'utils/constants'

const calculateDrawerTop = ({ bannerMargin }: { bannerMargin: boolean }) => {
  const top = bannerMargin ? BANNER_HEIGHT : 0
  return top
}

const useStyles = makeStyles(() => ({
  drawer: {
    width: DRAWER_WIDTH,
    flexShrink: 0,
  },
  drawerPaper: (props: { bannerMargin: boolean }) => ({
    width: DRAWER_WIDTH,
    paddingTop: 46,
    backgroundColor: tokens.surfaceColorDefault,
    overflow: 'visible',
    top: calculateDrawerTop(props),
  }),
  closeButton: {
    position: 'relative',
    top: -4,
  },
  startIcon: {
    position: 'relative',
    color: tokens.neutral400,
    marginRight: tokens.spacing02,
    marginTop: tokens.spacing01,
  },
  drawerHeader: {
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacing04,
  },
  drawerPaperAnchorDockedRight: {
    borderColor: tokens.interactiveColorNeutralDefault,
  },
}))

interface DrawerProps extends MuiDrawerProps {
  onCloseDrawer: () => void
  title?: string
  subtitle?: string
  startTitleIcon?: ReactNode
  bannerMargin?: boolean
}

const Drawer: FC<DrawerProps> = ({
  open,
  onCloseDrawer,
  title,
  children,
  subtitle,
  startTitleIcon,
  bannerMargin = false,
}) => {
  const classes = useStyles({ bannerMargin })
  const t = useTranslate()

  return (
    <Box
      id={
        'unified-admin-side-drawer'
        // Please don't change the ID.  Sub apps currently rely on it to know whether the drawer is open or not.
        // This is not ideal and should be it's own mechanism but it's better to have this hack than nothing at all.
        // Sames goes for data-is-open.
        // See: https://github.com/jive/unified-admin-ordering/commit/50f2a8472edc88d156494f28dd0e9a3fb1552597
      }
      className={classes.drawer}
      data-is-open={open}
    >
      <MuiDrawer
        classes={{
          paper: classes.drawerPaper,
          paperAnchorDockedRight: classes.drawerPaperAnchorDockedRight,
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        {(title || subtitle) && (
          <Box className={classes.drawerHeader}>
            {title && (
              <Grid container justifyContent="space-between" spacing={2}>
                <Grid item>
                  <Box display="flex">
                    {startTitleIcon && <SvgIcon className={classes.startIcon}>{startTitleIcon}</SvgIcon>}
                    <Typography data-testid="drawerTitle" variant="heading-small">
                      {title}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item>
                  <>
                    <IconButton
                      id="close-drawer-icon"
                      size="small"
                      className={classes.closeButton}
                      onClick={onCloseDrawer}
                      data-testid="drawer-close-btn"
                    >
                      <CloseOutlinedIcon />
                    </IconButton>
                  </>

                  <Tooltip triggerId="close-drawer-icon">
                    <span>{t('Close')}</span>
                  </Tooltip>
                </Grid>
              </Grid>
            )}
            {subtitle && (
              <Typography variant="caption-medium" color="type-color-secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
        {children}
      </MuiDrawer>
    </Box>
  )
}

export default memo(Drawer)
